<template>
	<div v-if="node.facet">
		<h-disclosure v-slot="{open}" :default-open="isNodeOpen">
			<div class="flex items-center space-x-2">
				<!-- Show chevron only if node has children -->
				<h-disclosure-button
					v-if="node.children && node.children.length > 0"
					class="ring-focus flex w-5 items-center justify-center space-x-1 outline-hidden focus:ring-offset-2"
				>
					<v-icon
						icon="chevron-right"
						class="size-4 transition-transform"
						:class="{'rotate-90': open}"
					/>
				</h-disclosure-button>
				<!-- Spacer when no children -->
				<div v-else class="w-5" />

				<h-switch
					v-slot="{checked}"
					:model-value="isNodeChecked"
					:value="node.facet.id"
					class="ring-focus group flex flex-1 cursor-pointer items-center space-x-2 text-left text-sm outline-hidden focus:ring-offset-2"
					@update:model-value="handleToggle"
				>
					<span
						class="flex size-4 items-center justify-center rounded text-white transition-colors duration-150"
						:class="{
							'bg-black': checked || parentChecked,
							'bg-gray-300': !checked && !parentChecked && isIndeterminate,
							'border-2 border-solid border-gray-100':
								!checked && !parentChecked && !isIndeterminate,
						}"
					>
						<transition
							enter-active-class="transition-opacity duration-150"
							enter-from-class="opacity-0"
							enter-to-class="opacity-100"
							leave-active-class="transition-opacity duration-150"
							leave-from-class="opacity-100"
							leave-to-class="opacity-0"
						>
							<v-icon
								v-show="checked || parentChecked || isIndeterminate"
								:icon="isIndeterminate ? 'minus' : 'check'"
								class="text-xs"
							/>
						</transition>
					</span>

					<span>
						{{ node.facet.label }}
						<span v-if="node.facet.count > 0" class="text-gray-400">
							({{ node.facet.count }})
						</span>
					</span>
				</h-switch>
			</div>

			<!-- Recursively render children -->
			<h-disclosure-panel
				v-if="node.children && node.children.length > 0"
				class="mt-xs ml-6 space-y-xs"
			>
				<category-tree-node
					v-for="child in node.children"
					:key="child.id"
					:node="child"
					:selected-facets="selectedFacets"
					:parent-checked="isNodeChecked || parentChecked"
					:parent-id="isNodeChecked ? node.facet.id : parentId"
					@toggle="handleChildToggle"
				/>
			</h-disclosure-panel>
		</h-disclosure>
	</div>
</template>

<script lang="ts" setup>
import type {CategoryTreeNodeWithFacet} from "./category-tree.vue";

const props = defineProps<{
	node: CategoryTreeNodeWithFacet;
	selectedFacets?: (string | number | boolean)[];
	parentChecked?: boolean;
	parentId?: string;
}>();

const emit = defineEmits<{
	(
		event: "toggle",
		nodeId: string,
		value: boolean,
		childIds: string[],
		parentId?: string,
	): void;
}>();

// Get all child IDs for this node
const childIds = computed(() => {
	const ids: string[] = [];

	const collectIds = (n: CategoryTreeNodeWithFacet) => {
		if (n.facet) {
			ids.push(n.facet.id);
		}
		if (n.children) {
			for (const child of n.children) {
				collectIds(child);
			}
		}
	};

	if (props.node.children) {
		for (const child of props.node.children) {
			collectIds(child);
		}
	}

	return ids;
});

const isNodeChecked = computed(() => {
	if (!props.node.facet) return false;
	// Don't show as checked if parent is checked (visual only)
	if (props.parentChecked) return false;
	return props.selectedFacets?.includes(props.node.facet.id) || false;
});

const isIndeterminate = computed(() => {
	if (!props.node.facet) return false;
	// Show indeterminate state when children are selected but not this node
	if (isNodeChecked.value) return false;
	return childIds.value.some((id) => props.selectedFacets?.includes(id));
});

const isNodeOpen = computed(() => {
	if (!props.node.facet) return false;
	// Auto-open if this node or any children are selected
	if (isNodeChecked.value) return true;
	return childIds.value.some((id) => props.selectedFacets?.includes(id));
});

const handleToggle = (value: boolean) => {
	if (!props.node.facet) return;
	// Special case: if parent is checked and we're clicking this child
	// We need to pass the parentId so we can remove parent and add only this child
	if (props.parentChecked && props.parentId) {
		emit("toggle", props.node.facet.id, true, childIds.value, props.parentId);
	} else {
		emit("toggle", props.node.facet.id, value, childIds.value);
	}
};

const handleChildToggle = (
	nodeId: string,
	value: boolean,
	childNodeIds: string[],
	parentId?: string,
) => {
	emit("toggle", nodeId, value, childNodeIds, parentId);
};
</script>
